<template>
  <div class="topup-page">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-start mb-3">
      <div>
        <h3 class="page-title mb-1">Пополнить баланс</h3>
        <div class="page-subtitle">
          Заявка проходит проверку оператора — обычно занимает несколько минут.
        </div>
      </div>
      <b-button variant="outline-light" class="btn-round" @click="$router.push('/userwallet')">
        ← К балансам
      </b-button>
    </div>

    <!-- Stepper -->
    <div class="stepper mb-4" v-if="step < 4">
      <div class="step-item" v-for="s in steps" :key="s.n"
           :class="{ 'is-active': step === s.n, 'is-done': step > s.n }">
        <div class="step-dot">{{ step > s.n ? '✓' : s.n }}</div>
        <div class="step-label">{{ s.label }}</div>
      </div>
    </div>

    <div class="card-dark topup-card">
      <!-- ШАГ 1 — выбор актива и сети -->
      <template v-if="step === 1">
        <div class="card-title-row">
          <div class="card-title">Выберите цифровой актив для пополнения</div>
          <div class="card-sub">
            Выберите цифровой актив, который хотите перевести на свой счёт Payton.kz.
            Если для актива доступно несколько сетей, убедитесь, что сеть перевода в вашем
            кошельке полностью совпадает с выбранной. Использование другой сети может привести
            к безвозвратной потере средств.
          </div>
        </div>

        <div class="form-group">
          <label class="lbl">Цифровой актив</label>
          <select class="select-dark" v-model="form.asset" @change="onAssetChange">
            <option value="" disabled>Выберите актив</option>
            <option v-for="asset in cryptoCatalog" :key="asset.code" :value="asset.code">
              {{ asset.code }} — {{ asset.name }}
            </option>
          </select>
        </div>

        <div class="form-group" v-if="availableNetworks.length > 1">
          <label class="lbl">Сеть</label>
          <select class="select-dark" v-model="form.network" @change="onNetworkChange">
            <option value="" disabled>Выберите сеть</option>
            <option v-for="net in availableNetworks" :key="net.networkCode" :value="net.networkCode">
              {{ net.networkName }}{{ net.tokenStandard ? ` (${net.tokenStandard})` : '' }}
            </option>
          </select>
          <small class="hint">Сеть нельзя поменять после отправки — деньги уйдут именно на этот адрес.</small>
        </div>
        <div class="form-group" v-else-if="availableNetworks.length === 1">
          <label class="lbl">Сеть</label>
          <div class="select-dark readonly">
            {{ availableNetworks[0].networkName }}{{ availableNetworks[0].tokenStandard ? ` (${availableNetworks[0].tokenStandard})` : '' }}
          </div>
        </div>

        <div class="form-group">
          <label class="lbl">Планируемое количество (необязательно)</label>
          <div class="amount-wrap">
            <input type="number" step="any" min="0" class="amount-input"
                   v-model.number="form.amount" placeholder="0.00" />
            <span class="amount-suffix">{{ form.asset || '—' }}</span>
          </div>
          <small class="hint">Можно не указывать — оператор зачислит фактически пришедшую сумму.</small>
        </div>

        <b-button block class="cta-btn mt-3" variant="primary"
                  :disabled="!form.asset || !form.network || busy" @click="goToTransfer">
          <b-spinner small v-if="busy" />
          <span v-else>Я определился с выбором</span>
        </b-button>
      </template>

      <!-- ШАГ 2 — перевод актива -->
      <template v-else-if="step === 2">
        <div class="card-title-row">
          <div class="card-title">Переведите цифровой актив</div>
          <div class="card-sub">
            Переведите выбранный цифровой актив только по указанному адресу и только через
            выбранную сеть. После отправки сохраните идентификатор транзакции (TX Hash) —
            он понадобится для подтверждения перевода.
          </div>
        </div>

        <div v-if="walletDetails">
          <div class="kv-row">
            <div class="kv-k">Адрес цифрового кошелька Payton.kz</div>
            <div class="kv-v mono">
              {{ walletDetails.address || '—' }}
              <button class="copy-btn" @click="copy(walletDetails.address)" v-if="walletDetails.address">Скопировать</button>
            </div>
          </div>
          <div class="kv-row">
            <div class="kv-k">Сеть</div>
            <div class="kv-v">{{ walletDetails.networkName || form.network }}{{ walletDetails.tokenStandard ? ` (${walletDetails.tokenStandard})` : '' }}</div>
          </div>
          <div class="warn-box mt-3">
            <b-icon icon="exclamation-triangle-fill" class="warn-ic mr-2" />
            <span>
              Отправьте крипто строго в сети <b>{{ walletDetails.networkName || form.network }}</b>.
              Перевод в другой сети будет утерян безвозвратно.
            </span>
          </div>
        </div>
        <div v-else class="card-hint">
          <b-icon icon="exclamation-circle" class="mr-2" />
          Для этой сети ещё не задан депозит-адрес. Свяжитесь с поддержкой.
        </div>

        <div class="nav-row mt-3">
          <b-button variant="outline-light" class="btn-round" @click="back">← Назад</b-button>
          <b-button class="cta-btn" variant="primary" :disabled="!(walletDetails && walletDetails.address)" @click="step = 3">
            Перевод выполнен
          </b-button>
        </div>
      </template>

      <!-- ШАГ 3 — подтверждение перевода (TX Hash) -->
      <template v-else-if="step === 3">
        <div class="card-title-row">
          <div class="card-title">Подтвердите перевод</div>
          <div class="card-sub">
            Вставьте TX Hash выполненного перевода. Мы используем его для поиска вашей
            транзакции в блокчейне и последующей проверки перед зачислением средств.
          </div>
        </div>

        <div class="mini-summary mb-3">
          {{ form.asset }} · {{ walletDetails && walletDetails.networkName || form.network }} · заявка №{{ depositId }}
        </div>

        <div class="form-group">
          <label class="lbl">TX Hash перевода *</label>
          <input type="text" class="text-input" :class="{ 'input-invalid': showErr && !form.proofRef }"
                 v-model.trim="form.proofRef" placeholder="Хэш транзакции — оператор сверит с блокчейном" />
          <small class="hint">
            Обязательно. На общий адрес приходят переводы многих клиентов —
            именно TX Hash связывает ваше поступление с заявкой.
            <a href="javascript:void(0)" @click="showTxHelp = !showTxHelp">Где найти TX Hash?</a>
          </small>
          <div v-if="showTxHelp" class="tx-help">
            TX Hash (Transaction Hash) — уникальный идентификатор транзакции в блокчейне.
            В кошельке (Trust Wallet, MetaMask, Binance) откройте историю → нужный перевод →
            «Детали» / «View on Explorer» → скопируйте длинную строку вида 0x1a2b… или буквенно-цифровой хэш.
          </div>
        </div>

        <div class="nav-row mt-3">
          <b-button variant="outline-light" class="btn-round" @click="back">← Назад</b-button>
          <b-button class="cta-btn" variant="primary" :disabled="busy" @click="submitConfirm">
            <b-spinner small v-if="busy" />
            <span v-else>Подтвердить</span>
          </b-button>
        </div>
      </template>

      <!-- ШАГ 4 — проверка Payton и зачисление -->
      <template v-else>
        <div class="card-title-row">
          <div class="success-ic mb-2">✓</div>
          <div class="card-title">Проверка Payton и зачисление средств</div>
          <div class="card-sub">
            После получения транзакции Payton проверит поступление средств, выполнит обязательную
            проверку KYT и после успешного завершения проверки зачислит цифровые активы на ваш
            баланс. Обычно процесс занимает от нескольких минут до одного часа — в зависимости
            от выбранной сети и нагрузки блокчейна.
          </div>
        </div>

        <div class="kv-row">
          <div class="kv-k">Заявка</div>
          <div class="kv-v">№{{ depositId }}</div>
        </div>
        <div class="kv-row">
          <div class="kv-k">Фактическое количество к зачислению</div>
          <div class="kv-v">
            <template v-if="form.amount > 0">≈ {{ form.amount }} {{ form.asset }} <span class="muted-note">(ожидается)</span></template>
            <template v-else>определяется после проверки</template>
          </div>
        </div>

        <div class="explain-block mt-3">
          <a href="javascript:void(0)" @click="showKytHelp = !showKytHelp">Почему проводится KYT?</a>
          <div v-if="showKytHelp" class="tx-help">
            В соответствии с требованиями регулятора AFSA, Payton обязан проверять поступающие
            цифровые активы на предмет связи с мошенничеством, санкциями и иной противоправной
            деятельностью. Проверка выполняется автоматически и не требует дополнительных
            действий с вашей стороны.
          </div>
        </div>

        <div class="explain-block mt-2">
          <a href="javascript:void(0)" @click="showConfHelp = !showConfHelp">Почему нужно дождаться подтверждений сети?</a>
          <div v-if="showConfHelp" class="tx-help">
            После отправки цифрового актива блокчейн должен подтвердить транзакцию. Количество
            необходимых подтверждений зависит от выбранной сети. До получения требуемого
            количества подтверждений средства не могут быть зачислены на ваш баланс.
          </div>
        </div>

        <div class="nav-row mt-4 justify-content-center">
          <b-button variant="outline-light" class="btn-round" @click="resetWizard">Ещё пополнение</b-button>
          <b-button class="cta-btn" variant="primary" @click="$router.push('/userwallet')">К балансам</b-button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { apiDomain, getHeader } from '@/config/config'
import { mapState } from 'vuex'

const WIZARD_KEY = 'topup_wizard_v1'

export default {
  name: 'TopUp',
  data() {
    return {
      step: 1,
      steps: [
        { n: 1, label: 'Выбор актива' },
        { n: 2, label: 'Перевод' },
        { n: 3, label: 'Подтверждение' },
      ],
      form: { asset: '', network: '', amount: null, proofRef: '' },
      cryptoCatalog: [],
      walletDetails: null,
      depositId: 0,
      createdFor: '',   // 'asset:network', под который создан текущий depositId
      busy: false,
      showTxHelp: false,
      showKytHelp: false,
      showConfHelp: false,
      showErr: false,
    }
  },
  computed: {
    ...mapState(['loginedUser']),
    availableNetworks() {
      const asset = this.cryptoCatalog.find((a) => a.code === this.form.asset)
      if (!asset) return []
      return asset.networks || []
    },
  },
  watch: {
    step() { this.saveState() },
    depositId() { this.saveState() },
    walletDetails() { this.saveState() },
    form: { handler() { this.saveState() }, deep: true },
  },
  async mounted() {
    // Заморозка аккаунта закрывает пополнение — не пускаем в визард
    // (единая точка для всех входов: главная, «Мои балансы», ссылки).
    if (await this.blockedByFreeze()) return
    this.fetchCatalog()
    // Возврат к незавершённой заявке из «Истории пополнений» (или из
    // ссылки в уведомлении): /topup?deposit={id} → открываем шаг 3 для
    // конкретного черновика. Имеет приоритет над локальным снимком.
    if (this.$route.query.deposit) {
      const resumed = await this.resumeFromQuery()
      if (resumed) return
    }
    this.restoreState()
  },
  methods: {
    // Проверка заморозки аккаунта перед входом в визард пополнения. При
    // заморозке — тост + возврат к балансам. Сбой сети → не блокируем
    // (бэкенд всё равно вернёт 403 на создание заявки).
    async blockedByFreeze() {
      try {
        const uid = this.loginedUser && this.loginedUser.id
        if (!uid) return false
        const { data } = await axios.get(`${apiDomain}/exchange/userdetails/${uid}`, { headers: getHeader() })
        const f = data && data.accountFrozen
        if (f === 1 || f === '1' || f === true) {
          this.toast('Аккаунт заморожен — пополнение временно недоступно. Обратитесь в поддержку: support@payton.kz', 'Сервис недоступен', 'warning')
          this.$router.replace('/userwallet').catch(() => {})
          return true
        }
      } catch (e) { /* не блокируем при сбое сети */ }
      return false
    },
    // Подтягивает конкретный крипто-черновик по id из query и переводит
    // визард на шаг 3 (ввод TX Hash). Возвращает true, если удалось.
    async resumeFromQuery() {
      const id = Number(this.$route.query.deposit)
      if (!id) return false
      try {
        const { data } = await axios.get(`${apiDomain}/funding/deposits`, { headers: getHeader() })
        const list = Array.isArray(data) ? data : []
        const dep = list.find((d) => d.depositId === id)
        // Дозаполнить можно только незавершённый крипто-черновик без хэша.
        if (!dep || dep.status !== 'PENDING' || dep.txHash || dep.paymentMethod !== 'ONCHAIN') {
          this.toast('Эту заявку нельзя дозаполнить.', 'Пополнение', 'warning')
          return false
        }
        this.form.asset = dep.asset
        this.form.network = dep.network
        if (dep.amount != null) this.form.amount = dep.amount
        this.depositId = dep.depositId
        this.createdFor = dep.asset + ':' + dep.network
        await this.loadWallet()
        this.step = 3
        return true
      } catch (e) {
        this.toast('Не удалось загрузить заявку.', 'Ошибка', 'danger')
        return false
      }
    },
    // Сохраняем прогресс визарда — заявка уже создана на бэке,
    // при F5 клиент не должен терять адрес и номер заявки.
    saveState() {
      try {
        if (this.step < 2 || this.step >= 4 || !this.depositId) {
          localStorage.removeItem(WIZARD_KEY)
          return
        }
        localStorage.setItem(WIZARD_KEY, JSON.stringify({
          step: this.step,
          form: this.form,
          depositId: this.depositId,
          createdFor: this.createdFor,
          walletDetails: this.walletDetails,
        }))
      } catch (e) { /* localStorage недоступен — не критично */ }
    },
    restoreState() {
      try {
        const raw = localStorage.getItem(WIZARD_KEY)
        if (!raw) return
        const saved = JSON.parse(raw)
        if (!saved || !saved.depositId || saved.step < 2 || saved.step >= 4) return
        this.step = saved.step
        this.form = { ...this.form, ...(saved.form || {}) }
        this.depositId = saved.depositId
        this.createdFor = saved.createdFor || ''
        this.walletDetails = saved.walletDetails || null
      } catch (e) { /* повреждённый снимок — стартуем с нуля */ }
    },
    fetchCatalog() {
      axios.get(`${apiDomain}/exchange/asset-networks`)
        .then((res) => {
          this.cryptoCatalog = Array.isArray(res.data) ? res.data : []
          if (!this.form.asset && this.cryptoCatalog.length) {
            this.form.asset = this.cryptoCatalog[0].code
            this.onAssetChange()
          }
        })
        .catch(() => { this.cryptoCatalog = [] })
    },
    onAssetChange() {
      const nets = this.availableNetworks
      if (nets.length === 1) {
        this.form.network = nets[0].networkCode
      } else if (nets.length > 1) {
        const def = nets.find((n) => n.isDefault) || nets[0]
        this.form.network = def ? def.networkCode : ''
      } else {
        this.form.network = ''
      }
    },
    onNetworkChange() { /* адрес тянем на шаге «Далее» */ },
    back() {
      if (this.step > 1) this.step -= 1
    },
    // Шаг 1 → 2: создаём заявку (или переиспользуем, если актив/сеть не
    // менялись), затем тянем депозит-адрес по сети.
    async goToTransfer() {
      if (!this.form.asset || !this.form.network || this.busy) return
      const key = this.form.asset + ':' + this.form.network
      this.busy = true
      try {
        if (!this.depositId || this.createdFor !== key) {
          const { data } = await axios.post(
            `${apiDomain}/funding/deposit-request`,
            { asset: this.form.asset, network: this.form.network, paymentMethod: 'ONCHAIN', amount: Number(this.form.amount) || 0, bankReference: '', txHash: '' },
            { headers: getHeader() },
          )
          this.depositId = (data && data.depositId) || 0
          this.createdFor = key
        }
        await this.loadWallet()
        this.step = 2
      } catch (e) {
        const sc = e && e.response && e.response.status
        const raw = (e && e.response && typeof e.response.data === 'string') ? e.response.data : ''
        if (sc === 403 && raw.indexOf('frozen') !== -1) {
          this.toast('Аккаунт заморожен — пополнение временно недоступно. Обратитесь в поддержку: support@payton.kz', 'Сервис недоступен', 'warning')
          this.$router.replace('/userwallet').catch(() => {})
        } else {
          this.toast('Не удалось создать заявку. Попробуйте позже.', 'Ошибка', 'danger')
        }
      } finally {
        this.busy = false
      }
    },
    async loadWallet() {
      try {
        const { data } = await axios.get(
          `${apiDomain}/funding/company-wallet/${this.form.asset}/${this.form.network}`,
          { headers: getHeader() },
        )
        this.walletDetails = data || null
      } catch (e) {
        this.walletDetails = null
      }
    },
    // Шаг 3 → 4: дополняем заявку TX Hash'ем (+ суммой).
    async submitConfirm() {
      this.showErr = true
      if (!this.form.proofRef || !String(this.form.proofRef).trim()) return
      this.busy = true
      try {
        await axios.post(
          `${apiDomain}/funding/deposit/${this.depositId}/complete`,
          { txHash: this.form.proofRef, amount: Number(this.form.amount) || 0 },
          { headers: getHeader() },
        )
        this.step = 4
      } catch (err) {
        const sc = err && err.response && err.response.status
        const raw = (err && err.response && typeof err.response.data === 'string') ? err.response.data : ''
        if (sc === 403 && raw.indexOf('frozen') !== -1) {
          this.toast('Аккаунт заморожен — пополнение временно недоступно. Обратитесь в поддержку: support@payton.kz', 'Сервис недоступен', 'warning')
          this.$router.replace('/userwallet').catch(() => {})
          return
        }
        let msg = 'Не удалось отправить заявку.'
        if (raw.indexOf('tx-hash-required') !== -1) msg = 'Укажите TX Hash перевода.'
        else if (raw.indexOf('deposit-not-pending') !== -1) msg = 'Заявка уже обработана или устарела — создайте новую.'
        this.toast(msg, 'Ошибка', 'danger')
      } finally {
        this.busy = false
      }
    },
    resetWizard() {
      this.step = 1
      this.form.amount = null
      this.form.proofRef = ''
      this.depositId = 0
      this.createdFor = ''
      this.showErr = false
      this.showTxHelp = false
      this.showKytHelp = false
      this.showConfHelp = false
    },
    copy(value) {
      if (!value) return
      navigator.clipboard.writeText(String(value)).then(() => {
        this.toast('Скопировано', 'OK', 'info')
      })
    },
    toast(message, title, variant) {
      this.$bvToast.toast(message, {
        variant: variant || 'info', solid: true, autoHideDelay: variant === 'info' ? 1400 : 4500,
        title, toaster: 'b-toaster-top-center',
      })
    },
  },
}
</script>

<style scoped>
.topup-page { margin-top: 18px; padding-bottom: 60px; max-width: 640px; }
.page-title { color: #FCFCFD; font-weight: 800; letter-spacing: -0.3px; margin-top: 6px; }
.page-subtitle { color: #B1B5C3; font-size: 13px; max-width: 540px; }
.btn-round { border-radius: 12px; padding: 8px 14px; }

.stepper { display: flex; align-items: center; gap: 8px; }
.step-item { display: flex; align-items: center; gap: 8px; flex: 1; }
.step-dot {
  width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font: 700 12px/1 Inter; color: #777e90;
  border: 1.5px solid rgba(119,126,144,.35); background: rgba(35,38,47,.55);
}
.step-label { font-size: 12px; color: #777e90; }
.step-item.is-active .step-dot { border-color: #efb914; color: #efb914; }
.step-item.is-active .step-label { color: #fcfcfd; font-weight: 700; }
.step-item.is-done .step-dot { border-color: #16A37F; color: #16A37F; }

.card-dark {
  background: rgba(35,38,47,.55);
  border: 1px solid rgba(119,126,144,.18);
  border-radius: 16px;
  padding: 18px;
}
.topup-card { min-height: 220px; }
.card-title-row { margin-bottom: 14px; }
.card-title { color: #FCFCFD; font: 700 16px/1.2 Inter; }
.card-sub { color: #777e90; font-size: 12px; margin-top: 4px; }
.card-hint { color: #B1B5C3; font-size: 13px; display: flex; align-items: center; padding: 16px 0; }

.form-group { margin-bottom: 14px; }
.lbl {
  display: block; color: var(--Neutrals-5,#b1b5c3);
  font: 700 12px/1 Inter; text-transform: uppercase; letter-spacing: .02em; margin-bottom: 6px;
}
.select-dark, .text-input, .amount-input {
  width: 100%; background: transparent;
  border: 2px solid var(--Neutrals-3,#353945); border-radius: 12px;
  color: #fcfcfd; padding: 9px 12px; font: 500 14px/1.4 Inter; outline: none;
}
.select-dark option { background: #18191d; color: #fcfcfd; }
.select-dark.readonly { color: #b1b5c3; cursor: default; background: rgba(53,57,69,.35); }
.text-input::placeholder { color: #6B7280; }
.text-input:focus, .select-dark:focus, .amount-input:focus {
  border-color: rgba(239,185,20,.5); box-shadow: 0 0 0 3px rgba(239,185,20,.12);
}
.input-invalid { border-color: rgba(239,68,68,.55) !important; }
.tx-help {
  margin-top: 8px; padding: 10px 12px;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(119,126,144,0.2);
  border-radius: 8px; font-size: 12px; line-height: 1.5; color: #B1B5C3;
}

.mini-summary {
  font-size: 12px; color: #B1B5C3;
  background: rgba(96,165,250,.08); border: 1px solid rgba(96,165,250,.25);
  border-radius: 10px; padding: 8px 12px;
}

.explain-block a { color: #efb914; font-size: 13px; font-weight: 600; text-decoration: none; }
.explain-block a:hover { text-decoration: underline; }
.muted-note { color: #777e90; font-size: 11px; }

.amount-wrap { position: relative; display: flex; align-items: center; }
.amount-input { flex: 1; padding-right: 60px; }
.amount-suffix {
  position: absolute; right: 14px; color: #b1b5c3;
  font: 700 13px/1 Inter; text-transform: uppercase; pointer-events: none;
}

.hint { color: #777e90; font-size: 11px; line-height: 1.4; display: block; margin-top: 4px; }

.cta-btn {
  border-radius: 12px; font-weight: 700; padding: 11px 16px;
  background: #efb914; border-color: #efb914; color: #1a1d22;
}
.cta-btn:hover { filter: brightness(1.05); }
.cta-btn:disabled { opacity: 0.55; }

.nav-row { display: flex; justify-content: space-between; gap: 10px; }

.kv-row {
  display: flex; justify-content: space-between; align-items: baseline;
  padding: 9px 0; border-bottom: 1px solid rgba(119,126,144,.15); gap: 12px;
}
.kv-row:last-of-type { border-bottom: none; }
.kv-k { color: #777e90; font-size: 12px; font-weight: 600; min-width: 130px; }
.kv-v { color: #fcfcfd; font-size: 13px; font-weight: 600; text-align: right; word-break: break-all; }
.kv-v.mono { font-family: ui-monospace, SFMono-Regular, monospace; }
.copy-btn {
  background: rgba(239,185,20,.18); border: none; color: #efb914;
  font: 700 10px/1 Inter; text-transform: uppercase; padding: 4px 8px;
  border-radius: 6px; cursor: pointer; margin-left: 8px;
}
.copy-btn:hover { background: rgba(239,185,20,.28); }

.warn-box {
  background: rgba(239,185,20,.08); border: 1px solid rgba(239,185,20,.35);
  color: #fcfcfd; border-radius: 12px; padding: 12px 14px;
  font-size: 12px; line-height: 1.5; display: flex; align-items: flex-start;
}
.warn-ic { color: #efb914; flex-shrink: 0; }

.success-block { text-align: center; padding: 24px 8px; }
.success-ic {
  width: 56px; height: 56px; margin: 0 auto 14px; border-radius: 50%;
  background: rgba(16,185,129,.12); border: 1px solid rgba(16,185,129,.45);
  color: #16A37F; font-size: 26px; display: flex; align-items: center; justify-content: center;
}
.success-title { color: #FCFCFD; font: 800 18px/1.2 Inter; margin-bottom: 8px; }
.success-sub { color: #B1B5C3; font-size: 13px; line-height: 1.5; max-width: 420px; margin: 0 auto; }
</style>
